import { Checkbox, ListItem, ListItemText, TextField, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { useState } from "react";
import AutocompleteCreatable from "@entrilia/shared/components/AutocompleteCreatable";
import useDebounce from "@entrilia/shared/hooks/useDebounce";

interface Props<T> {
  options: T[];
  values: T[];
  noItemsText?: string;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  onSelected: (items: T[]) => void;
  getOptionLabel: (option: T) => string;
  getOptionValue: (option: T) => string;
  getOptionDescription?: (option: T) => string;
  getOptionSecondaryText?: (option: T) => string;
  onCreateOption?: () => void;
  groupBy?: (option: T) => string;
  renderValue?: (values: T[]) => ReactNode;
  error?: boolean;
  helperText?: string;
  dense?: boolean;
}

const MultiselectAutocomplete = <T,>({
  options,
  values,
  noItemsText,
  placeholder,
  label,
  required,
  disabled,
  onSelected,
  getOptionLabel,
  getOptionValue,
  getOptionDescription,
  onCreateOption,
  groupBy,
  renderValue,
  error,
  helperText,
  dense,
}: Props<T>) => {
  const [search, setSearch] = useState("");

  const searchLowerCase = search.toLowerCase();
  const filtered = search
    ? options.filter(
        (option) =>
          (getOptionLabel(option) || "").toLowerCase().includes(searchLowerCase) ||
          (getOptionValue(option) || "").toLowerCase().includes(searchLowerCase) ||
          (getOptionDescription && (getOptionDescription(option) || "").toLowerCase().includes(searchLowerCase))
      )
    : options;

  const onInputChange = useDebounce((value: string) => {
    setSearch(value);
  }, 300);

  return (
    <AutocompleteCreatable
      noItemsText={noItemsText}
      withLazyRendering
      onCreateOption={onCreateOption}
      multiple
      fullWidth
      disabled={disabled}
      sx={(t) => ({ ".MuiInputBase-input": { color: t.palette.secondary.main } })}
      options={options}
      filterOptions={() => filtered}
      disableCloseOnSelect
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, item) => option && item && getOptionValue(option) === getOptionValue(item)}
      value={values}
      onChange={(event, selectedOptions) => {
        event.stopPropagation();
        onSelected(selectedOptions);
      }}
      onInputChange={(_, value, reason) => {
        if (reason === "input") {
          onInputChange(value);
        }
      }}
      onClose={() => setSearch("")}
      renderValue={renderValue}
      renderOption={({ key: _key, ...props }, option, { selected, index }) => (
        <ListItem
          key={index}
          {...props}
          value={getOptionValue(option)}
          dense={dense}
          style={dense ? { paddingTop: 0, paddingBottom: 0 } : undefined}
        >
          <Checkbox checked={selected} tabIndex={-1} disableRipple />
          <ListItemText
            slotProps={{ primary: { component: "span" } }}
            primary={
              <Typography variant={getOptionDescription === undefined ? "body1" : "subtitle2"}>
                {getOptionLabel(option)}
              </Typography>
            }
            secondary={getOptionDescription && getOptionDescription(option)}
          />
        </ListItem>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={values.length === 0 ? placeholder : undefined}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
        />
      )}
      groupBy={groupBy}
    />
  );
};

export default MultiselectAutocomplete;
